import { useState, useEffect } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Trophy, TrendUp, TrendDown, Briefcase, ListChecks, Warning, Users, EnvelopeSimple, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const GRADE_STYLES = {
  A: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border-emerald-300",
  B: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 border-blue-300",
  C: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 border-amber-300",
  D: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 border-red-300",
};

export default function OperativeScorecardPage() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("score");
  const [optOut, setOptOut] = useState(!!user?.operative_digest_opt_out);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestPreviewHtml, setDigestPreviewHtml] = useState("");

  useEffect(() => {
    API.get("/analytics/operative-scorecard").then(({ data }) => setScorecards(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggleOptOut = async (next) => {
    setOptOut(next);
    try { await API.post("/scheduler/operative-digest-opt-out", { opt_out: next }); toast.success(next ? "Unsubscribed from monthly digest" : "Re-subscribed to monthly digest"); }
    catch { toast.error("Failed to update preference"); setOptOut(!next); }
  };
  const sendDigestNow = async () => {
    if (!window.confirm("Send the monthly scorecard digest to every active in-house operative now?")) return;
    setSendingDigest(true);
    try {
      const { data } = await API.post("/scheduler/send-operative-digest");
      toast.success(`Sent to ${data.sent} operatives (skipped ${data.skipped})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSendingDigest(false); }
  };
  const previewMyDigest = async () => {
    try {
      const { data } = await API.get("/scheduler/operative-digest-preview");
      setDigestPreviewHtml(data.html);
    } catch (e) { toast.error("Couldn't load preview"); }
  };

  const sorted = [...scorecards].sort((a, b) => {
    if (sortBy === "score") return b.score - a.score;
    if (sortBy === "tasks") return b.total_tasks - a.total_tasks;
    if (sortBy === "jobs") return b.total_jobs - a.total_jobs;
    if (sortBy === "activity") return b.recent_activity_14d - a.recent_activity_14d;
    return 0;
  });

  return (
    <div data-testid="operative-scorecard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Operative Scorecard</h1>
          <p className="text-sm text-muted-foreground mt-1">Performance across your in-house team — based on jobs, tasks, recency, and overdue work.</p>
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[150px]" data-testid="op-scorecard-sort"><SelectValue placeholder="Sort by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="score">Score</SelectItem>
            <SelectItem value="tasks">Tasks</SelectItem>
            <SelectItem value="jobs">Jobs</SelectItem>
            <SelectItem value="activity">Recent activity</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Phase 232: monthly digest controls */}
      <div className="border border-border rounded-md p-3 mb-5 bg-card flex flex-wrap items-center gap-3" data-testid="op-digest-card">
        <div className="flex items-center gap-2">
          <EnvelopeSimple size={16} className="text-primary" />
          <p className="text-sm font-semibold">Monthly digest</p>
          <Badge variant="secondary" className="text-[10px]">1st of each month</Badge>
        </div>
        <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">A friendly email summarising the previous month's performance with your grade and a link to your full scorecard.</p>
        <div className="flex items-center gap-2">
          <Switch checked={!optOut} onCheckedChange={(v) => toggleOptOut(!v)} data-testid="op-digest-opt-toggle" />
          <Label className="text-xs cursor-pointer">{optOut ? "Off" : "On"}</Label>
        </div>
        <Button size="sm" variant="outline" onClick={previewMyDigest} data-testid="op-digest-preview-btn"><Eye size={12} className="mr-1" /> Preview mine</Button>
        {isAdmin && (
          <Button size="sm" onClick={sendDigestNow} disabled={sendingDigest} data-testid="op-digest-send-now"><EnvelopeSimple size={12} className="mr-1" /> {sendingDigest ? "Sending…" : "Send now to all"}</Button>
        )}
      </div>

      {digestPreviewHtml && (
        <div className="border border-border rounded-md p-3 mb-5 bg-muted/20" data-testid="op-digest-preview">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Preview</p>
            <Button size="sm" variant="ghost" onClick={() => setDigestPreviewHtml("")}>Close</Button>
          </div>
          <iframe title="digest-preview" srcDoc={digestPreviewHtml} className="w-full h-[480px] bg-white rounded border-0" sandbox="" />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <Users size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground text-sm">No in-house operatives yet — invite some from Settings.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sorted.map((v, idx) => (
            <div key={v.user_id} className="border border-border rounded-md p-4 bg-card hover:shadow-sm transition-shadow" data-testid={`op-scorecard-${v.user_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {idx < 3 && <Trophy size={16} className={idx === 0 ? "text-amber-500" : idx === 1 ? "text-gray-400" : "text-amber-700"} weight="fill" />}
                    <p className="text-sm font-semibold truncate">{v.name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{(v.role || "").replace(/_/g, " ")}</p>
                </div>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold border ${GRADE_STYLES[v.grade]}`}>{v.grade}</div>
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Score</span>
                  <span className="font-bold">{v.score}/100</span>
                </div>
                <Progress value={v.score} className="h-2" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
                  <ListChecks size={14} className="text-emerald-500" />
                  <div>
                    <p className="text-muted-foreground">Tasks done</p>
                    <p className="font-semibold">{v.completed_tasks}/{v.total_tasks}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
                  <Briefcase size={14} className="text-blue-500" />
                  <div>
                    <p className="text-muted-foreground">Jobs done</p>
                    <p className="font-semibold">{v.completed_jobs}/{v.total_jobs}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
                  <TrendUp size={14} className="text-violet-500" />
                  <div>
                    <p className="text-muted-foreground">14-day activity</p>
                    <p className="font-semibold">{v.recent_activity_14d}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
                  {v.overdue_tasks > 0 ? <Warning size={14} className="text-red-500" /> : <TrendDown size={14} className="text-emerald-500" />}
                  <div>
                    <p className="text-muted-foreground">Overdue</p>
                    <p className={`font-semibold ${v.overdue_tasks > 0 ? "text-red-600" : ""}`}>{v.overdue_tasks}</p>
                  </div>
                </div>
              </div>
              <div className="mt-2"><Badge variant="outline" className="text-[10px]">{v.email}</Badge></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
